import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concat, EMPTY, forkJoin, from, map, Observable, of, switchMap, take, withLatestFrom } from 'rxjs';

import {
  CompanyInfo,
  CompanyLocation,
  EmploymentType,
  InventoryEventFilters,
  PayClass,
} from '@core/models';
import { CompanyApiService } from '@core/data/api/company-api.service';
import { CatalogsApiService } from '@core/data/api/catalogs-api.service';
import { certificationFromDto, certificationToDto, softwareToolFromDto, softwareToolToDto } from '@core/data/api/catalogs.mapper';
import { InventoryApiService } from '@core/data/api/inventory-api.service';
import { EquipmentAssetDto, EventFilterDto } from '@core/data/api/inventory.dto';
import {
  assetFromDto,
  assetToDto,
  equipmentTypeFromDto,
  equipmentTypeToDto,
  eventFromDto,
  eventKindToDto,
  statusToDto,
} from '@core/data/api/inventory.mapper';
import { problemDetail } from '@core/data/api/api-error.interceptor';
import { CompanySyncService } from '@core/data/api/company-sync.service';
import {
  companyInfoFromDto,
  locationFromDto,
  locationsFromDto,
  payClassesFromDto,
  payClassFromDto,
} from '@core/data/api/company.mapper';
import { hasAddress } from '@shared/ui/inputs/address-fields/address-fields.component';
import { DataRepository } from '@core/data/data-repository';
import { CompanyApiActions, CompanyPageActions } from './company.actions';
import { selectInfo, selectInventoryEventFilters, selectInventoryEventsPage } from './company.reducer';

/**
 * Loads everything the Company page renders.
 *
 * THREE of the nine slices — the company profile (with its growth plan, Diamond
 * Standard and core values), the locations and the pay classes — are served by
 * `GET /api/v1/company` and come from there when an API is configured. The other
 * six (team, certifications, equipment types and assets, software tools,
 * employment types) have no contract yet and stay on the seeded repository until
 * they do.
 *
 * With no `apiUrl` configured, everything falls back to the repository, which is
 * what keeps the prototype demoing without a backend.
 */
@Injectable()
export class CompanyEffects {
  private readonly actions$ = inject(Actions);
  private readonly repo = inject(DataRepository);
  private readonly api = inject(CompanyApiService);
  private readonly sync = inject(CompanySyncService);
  private readonly inventory = inject(InventoryApiService);
  private readonly catalogs = inject(CatalogsApiService);
  private readonly store = inject(Store);

  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.entered),
      switchMap(() =>
        forkJoin({
          // One call covers the profile + phases + pillars + values + locations +
          // pay classes; the mapper splits it back into the slices the page reads.
          company: this.company$(),
          certifications: this.certifications$(),
          equipmentTypes: this.equipmentTypes$(),
          equipmentAssets: this.assets$(),
          softwareTools: this.softwareTools$(),
          employmentTypes: this.employmentTypes$(),
        }).pipe(
          map(({ company, ...rest }) => CompanyApiActions.loadSuccess({ ...rest, ...company })),
          catchError((err: unknown) =>
            of(CompanyApiActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * Done was clicked: replay the session's edits, then take the page the server
   * returns as the new truth. Nothing is sent when no API is configured — the
   * prototype keeps its session-only behaviour.
   */
  readonly saveEdits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.editDone),
      withLatestFrom(this.store.select(selectInfo)),
      switchMap(([{ working }, baseline]) => {
        if (!this.api.available() || !baseline) return EMPTY;
        return this.sync.save(baseline, working).pipe(
          // null = nothing changed, so there is nothing to replace.
          switchMap((dto) =>
            dto
              ? of(
                  CompanyApiActions.saveSuccess({
                    info: companyInfoFromDto(dto),
                    locations: locationsFromDto(dto),
                    payClasses: payClassesFromDto(dto),
                  }),
                )
              : EMPTY,
          ),
          catchError((err: unknown) => of(CompanyApiActions.saveFailure({ error: toMessage(err) }))),
        );
      }),
    ),
  );

  /**
   * Save one location. POST when it has no guid, PUT when it does.
   *
   * PUT REPLACES the row, so the body carries every field — including the four
   * the editor doesn't collect (`physical`, `timeZone`, `startDt`, `notes`),
   * round-tripped from what was loaded. Sending only name and address would null
   * them, which is the whole reason the verb is PUT rather than PATCH.
   */
  readonly saveLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.locationSaved),
      switchMap(({ location }) => {
        if (!this.api.available()) return EMPTY;
        const body = {
          name: location.name,
          address: hasAddress(location.address) ? location.address : null,
          physical: location.physical ?? true,
          timeZone: location.timeZone ?? null,
          startDt: location.startDt ?? '',
          notes: location.notes ?? '',
          // Sent on EVERY save, not just a retire. This is a replace, and `active`
          // is the field that takes a location out of use — omit it while renaming
          // one and the rename would retire it as a side effect.
          active: location.active ?? true,
        };
        const call = location.locationGuid
          ? this.api.replaceLocation(location.locationGuid, body)
          : this.api.createLocation(body);
        return call.pipe(
          map((dto) => CompanyApiActions.locationUpserted({ location: locationFromDto(dto) })),
          catchError((err: unknown) => of(CompanyApiActions.rowSaveFailed({ error: toMessage(err) }))),
        );
      }),
    ),
  );

  /**
   * Delete one location.
   *
   * A `409` is not an error to report and forget: it means people or seats still
   * point at the row, and the server's message names it and says to retire it
   * instead. That travels with the guid so the UI can offer exactly that, rather
   * than leaving the user with a refusal and nothing to do about it.
   */
  readonly deleteLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.locationRemoved),
      switchMap(({ location }) => {
        const locationGuid = location.locationGuid;
        if (!this.api.available() || !locationGuid) return EMPTY;
        return this.api.deleteLocation(locationGuid).pipe(
          map(() => CompanyApiActions.locationDeleted({ locationGuid })),
          catchError((err: unknown) =>
            of(
              err instanceof HttpErrorResponse && err.status === 409
                ? CompanyApiActions.locationDeleteBlocked({ locationGuid, message: toMessage(err) })
                : CompanyApiActions.saveFailure({ error: toMessage(err) }),
            ),
          ),
        );
      }),
    ),
  );

  /** Save one pay class. Also a replace — both of its fields are always sent. */
  readonly savePayClass$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.payClassSaved),
      switchMap(({ payClass }) => {
        if (!this.api.available()) return EMPTY;
        const body = { label: payClass.name, overtimeEligible: payClass.overtimeEligible };
        const call = payClass.payClassGuid
          ? this.api.replacePayClass(payClass.payClassGuid, body)
          : this.api.createPayClass(body);
        return call.pipe(
          map((dto) => CompanyApiActions.payClassUpserted({ payClass: payClassFromDto(dto) })),
          catchError((err: unknown) => of(CompanyApiActions.rowSaveFailed({ error: toMessage(err) }))),
        );
      }),
    ),
  );

  /**
   * Inventory writes.
   *
   * Each one returns the asset as the server now has it, and each is followed by
   * a re-read of the history — the server writes those rows itself, in the same
   * transaction, and there is deliberately no endpoint for posting one. A log the
   * client appended to separately would be wrong the first time one of the two
   * calls failed.
   */
  readonly saveAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.assetSaved),
      switchMap(({ asset, created, photo, removePhoto }) => {
        if (!this.inventory.available()) return EMPTY;
        const body = assetToDto(asset, created);
        const saved = created ? this.inventory.createAsset(body) : this.inventory.updateAsset(asset.assetGuid, body);
        // The modal collects a status, but PATCH cannot set one — it is its own
        // endpoint, because changing it is an event in the life of the thing.
        // So: write the facts, then move the status only if it actually differs.
        const want = statusToDto(asset.status);
        return this.afterAssetWrite(
          saved.pipe(
            switchMap((dto) =>
              dto.assetStatus === want ? of(dto) : this.inventory.changeStatus(dto.assetGuid, { assetStatus: want }),
            ),
            // A photo picked before the unit existed goes up now, against the guid
            // the server issued. The response IS the stored photo, so it is folded
            // into the asset rather than costing a re-read.
            //
            // A failure here does NOT fail the save: the unit exists by this point,
            // and reporting "save failed" would send somebody back to re-enter a
            // record that is already there. The interceptor has shown the dialog, so
            // the upload is reported without being conflated with the write.
            // The photo rides with the save because the form holds it until then
            // (standards §8i) — picked on a unit that exists, or picked before it
            // did and uploaded here against the guid the server issued.
            switchMap((dto) => {
              if (photo) {
                return this.inventory.uploadPhoto(dto.assetGuid, photo).pipe(
                  switchMap((uploaded) =>
                    // THE UPLOAD'S ANSWER IS NOT ALWAYS RENDERABLE. `downloadUrl`
                    // is signed per request, and where the upload reply carries no
                    // url the row took `undefined` and showed no photo until
                    // somebody reloaded — the picture was stored the whole time.
                    // So: use the url when there is one, and otherwise re-read the
                    // asset, which is the call that definitely signs one.
                    uploaded?.downloadUrl
                      ? of({ ...dto, photo: uploaded })
                      : this.inventory.listAssets().pipe(
                          map((all) => all.find((a) => a.assetGuid === dto.assetGuid) ?? dto),
                          catchError(() => of({ ...dto, photo: uploaded })),
                        ),
                  ),
                  catchError(() => of(dto)),
                );
              }
              if (removePhoto) {
                return this.inventory.deletePhoto(dto.assetGuid).pipe(
                  map(() => ({ ...dto, photo: null })),
                  catchError(() => of(dto)),
                );
              }
              return of(dto);
            }),
          ),
          // The modal is still open and shows this itself.
          (error) => CompanyApiActions.rowSaveFailed({ error }),
        );
      }),
    ),
  );

  readonly assignAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.assetAssigned),
      switchMap(({ assetGuid, personaCode, assignedDt, conditionOut }) =>
        this.inventory.available()
          ? // THE ASSET OUT OF THE MOVEMENT. Issuing answers `{ asset, assignment }`
            // because it writes both; mapping the movement as an asset produced a
            // unit with no guid, which the list showed as a blank extra row while
            // the real one stayed unchanged until a reload.
            this.afterAssetWrite(
              this.inventory
                .assign(assetGuid, { personaCode, assignedDt, conditionOut })
                .pipe(map((movement) => movement.asset)),
            )
          : EMPTY,
      ),
    ),
  );

  readonly returnAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.assetReturned),
      switchMap(({ assetGuid, returnedDt, conditionIn }) =>
        this.inventory.available()
          ? this.afterAssetWrite(
              this.inventory
                .returnAsset(assetGuid, { returnedDt, conditionIn })
                .pipe(map((movement) => movement.asset)),
            )
          : EMPTY,
      ),
    ),
  );

  readonly changeAssetStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.assetStatusChanged),
      switchMap(({ assetGuid, status }) =>
        this.inventory.available()
          ? this.afterAssetWrite(this.inventory.changeStatus(assetGuid, { assetStatus: statusToDto(status) }))
          : EMPTY,
      ),
    ),
  );

  readonly uploadAssetPhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.assetPhotoUploaded),
      switchMap(({ assetGuid, file }) =>
        this.inventory.available()
          ? this.inventory.uploadPhoto(assetGuid, file).pipe(
              switchMap(() => this.inventory.listAssets()),
              map((list) => CompanyApiActions.loadAssets({ assets: list.map(assetFromDto) })),
              // SAY SO. This swallowed the failure, so a photo that never reached
              // the server looked attached until the modal was reopened and the
              // picture was simply gone.
              catchError(() => of(CompanyApiActions.rowSaveFailed({ error: 'The photo could not be uploaded. It has not been saved.' }))),
            )
          : EMPTY,
      ),
    ),
  );

  readonly removeAssetPhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.assetPhotoRemoved),
      switchMap(({ assetGuid }) =>
        this.inventory.available()
          ? this.inventory.deletePhoto(assetGuid).pipe(
              switchMap(() => this.inventory.listAssets()),
              map((list) => CompanyApiActions.loadAssets({ assets: list.map(assetFromDto) })),
              catchError(() => of(CompanyApiActions.rowSaveFailed({ error: 'The photo could not be removed.' }))),
            )
          : EMPTY,
      ),
    ),
  );

  /**
   * Delete an asset.
   *
   * A `409` means somebody is holding it, and the server's message names them —
   * the interceptor shows it, so nothing extra is needed here beyond not
   * pretending the row went away.
   */
  readonly deleteAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.assetRemoved),
      switchMap(({ assetGuid }) => {
        if (!this.inventory.available()) return EMPTY;
        return this.inventory.deleteAsset(assetGuid).pipe(
          switchMap(() => concat(of(CompanyApiActions.assetDeleted({ assetGuid })), this.events$())),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  /** Take the server's asset, then re-read the history it just wrote. */
  private afterAssetWrite(write: Observable<EquipmentAssetDto>, onError?: (error: string) => Action) {
    return write.pipe(
      switchMap((dto) =>
        concat(
          of(CompanyApiActions.assetUpserted({ asset: assetFromDto(dto) })),
          this.events$(),
        ),
      ),
      // Without an `onError` the interceptor's dialog is the whole report and
      // there is nothing on screen waiting for an answer. With one, a modal is
      // open and the message belongs inside it.
      catchError((err: unknown) => (onError ? of(onError(toMessage(err))) : EMPTY)),
    );
  }

  /**
   * Page 0 of the history, under whatever filters are currently applied.
   *
   * Re-reading with no filters would silently widen what the user is looking at,
   * and re-reading only page 0 is deliberate: a new row is the newest, so it is
   * on the first page by definition.
   */
  private events$() {
    return this.store.select(selectInventoryEventFilters).pipe(
      take(1),
      switchMap((filters) => this.inventory.listEvents(toEventFilterDto(filters))),
      map((page) =>
        CompanyApiActions.eventsLoaded({
          events: page.content.map(eventFromDto),
          page: page.page,
          totalElements: page.totalElements,
          append: false,
        }),
      ),
    );
  }

  /** A filter change: read page 0 and replace what is on screen. */
  readonly filterEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.eventFiltersChanged),
      switchMap(({ filters }) =>
        this.inventory.available()
          ? this.inventory.listEvents(toEventFilterDto(filters)).pipe(
              map((page) =>
                CompanyApiActions.eventsLoaded({
                  events: page.content.map(eventFromDto),
                  page: page.page,
                  totalElements: page.totalElements,
                  append: false,
                }),
              ),
              catchError(() => EMPTY),
            )
          : EMPTY,
      ),
    ),
  );

  /** "Load more": the next page, appended. */
  readonly moreEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.moreEventsRequested),
      withLatestFrom(
        this.store.select(selectInventoryEventFilters),
        this.store.select(selectInventoryEventsPage),
      ),
      switchMap(([, filters, page]) =>
        this.inventory.available()
          ? this.inventory.listEvents(toEventFilterDto(filters), page + 1).pipe(
              map((next) =>
                CompanyApiActions.eventsLoaded({
                  events: next.content.map(eventFromDto),
                  page: next.page,
                  totalElements: next.totalElements,
                  append: true,
                }),
              ),
              catchError(() => EMPTY),
            )
          : EMPTY,
      ),
    ),
  );

  /**
   * Saving a kind of equipment.
   *
   * It has to go to the server even though the catalogue looks like local
   * reference data: an asset points at `equipmentItemGuid`, so a kind invented
   * here would hand every asset created against it an id the server has never
   * seen. That is the bug that made adding an asset fail to parse.
   */
  readonly saveEquipmentType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.equipmentTypeSaved),
      switchMap(({ equipmentType, created }) => {
        if (!this.inventory.available()) return EMPTY;
        const body = equipmentTypeToDto(equipmentType);
        const write = created
          ? this.inventory.createItem(body)
          : this.inventory.updateItem(equipmentType.equipmentGuid, body);
        return write.pipe(
          switchMap(() => this.equipmentTypes$()),
          map((equipmentTypes) => CompanyApiActions.equipmentTypesLoaded({ equipmentTypes })),
          catchError((err: unknown) => of(CompanyApiActions.rowSaveFailed({ error: toMessage(err) }))),
        );
      }),
    ),
  );

  /**
   * Saving a software tool. `/api/v1/software` takes POST and PATCH (admin).
   *
   * There is no equivalent for certifications — the contract publishes only the
   * two reads — so that editor stays local until there is somewhere to send it.
   */
  readonly saveSoftwareTool$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.softwareToolSaved),
      switchMap(({ softwareTool, created }) => {
        if (!this.catalogs.available()) return EMPTY;
        const body = softwareToolToDto(softwareTool);
        const write = created
          ? this.catalogs.createSoftware(body)
          : this.catalogs.updateSoftware(softwareTool.softwareGuid, body);
        return write.pipe(
          switchMap(() => this.softwareTools$()),
          map((softwareTools) => CompanyApiActions.softwareToolsLoaded({ softwareTools })),
          catchError((err: unknown) => of(CompanyApiActions.rowSaveFailed({ error: toMessage(err) }))),
        );
      }),
    ),
  );

  /**
   * Saving a certification. `/api/v1/certifications` takes POST and PATCH, and
   * both answer the WHOLE catalogue — so the response is the new list rather than
   * one row to splice in.
   */
  readonly saveCertification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.certificationSaved),
      switchMap(({ certification, created }) => {
        if (!this.catalogs.available()) return EMPTY;
        const body = certificationToDto(certification);
        const write = created
          ? this.catalogs.createCertification(body)
          : this.catalogs.updateCertification(certification.certificationGuid, body);
        return write.pipe(
          map((list) =>
            CompanyApiActions.certificationsLoaded({ certifications: list.map(certificationFromDto) }),
          ),
          catchError((err: unknown) => of(CompanyApiActions.rowSaveFailed({ error: toMessage(err) }))),
        );
      }),
    ),
  );

  /**
   * Deleting a catalogue row — the four that used to have no endpoint.
   *
   * One effect for all four because the shape is identical: call, re-read the
   * list, and on a 409 show the SERVER's words. That last part matters: a 409 here
   * means something still points at the row, and its `detail` names the way out
   * ("Retire it instead…"), which is more useful than anything we could compose.
   */
  readonly removeEmploymentType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.employmentTypeRemoved),
      switchMap(({ label }) => {
        if (!this.catalogs.available()) return EMPTY;
        // No guid on our side -- employment types are bare strings -- so the row
        // is found by matching the label against the list the server sent.
        return this.catalogs.listEmploymentTypes().pipe(
          switchMap((rows) => {
            const row = rows.find((r) => r.label === label);
            if (!row) return of(null);
            return this.catalogs.deleteEmploymentType(row.employmentTypeGuid);
          }),
          switchMap(() => this.employmentTypes$()),
          map((employmentTypes) => CompanyApiActions.employmentTypesLoaded({ employmentTypes })),
          catchError((err: unknown) =>
            of(CompanyApiActions.rowSaveFailed({ error: removalMessage(err, label) })),
          ),
        );
      }),
    ),
  );

  readonly removePayClass$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.payClassRemoved),
      switchMap(({ payClassGuid, label }) => {
        if (!this.api.available()) return EMPTY;
        return this.api.deletePayClass(payClassGuid).pipe(
          switchMap(() => this.payClasses$()),
          map((payClasses) => CompanyApiActions.payClassesLoaded({ payClasses })),
          catchError((err: unknown) =>
            of(CompanyApiActions.rowSaveFailed({ error: removalMessage(err, label) })),
          ),
        );
      }),
    ),
  );

  readonly removeSoftwareTool$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.softwareToolRemoved),
      switchMap(({ softwareGuid, label }) => {
        if (!this.catalogs.available()) return EMPTY;
        return this.catalogs.deleteSoftware(softwareGuid).pipe(
          switchMap(() => this.softwareTools$()),
          map((softwareTools) => CompanyApiActions.softwareToolsLoaded({ softwareTools })),
          catchError((err: unknown) =>
            of(CompanyApiActions.rowSaveFailed({ error: removalMessage(err, label) })),
          ),
        );
      }),
    ),
  );

  readonly removeEquipmentType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.equipmentTypeRemoved),
      switchMap(({ equipmentGuid, label }) => {
        if (!this.inventory.available()) return EMPTY;
        return this.inventory.deleteItem(equipmentGuid).pipe(
          switchMap(() => this.equipmentTypes$()),
          map((equipmentTypes) => CompanyApiActions.equipmentTypesLoaded({ equipmentTypes })),
          catchError((err: unknown) =>
            of(CompanyApiActions.rowSaveFailed({ error: removalMessage(err, label) })),
          ),
        );
      }),
    ),
  );

  /** Retiring a certification. Answers the catalogue, same as the writes do. */
  readonly removeCertification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.certificationRemoved),
      switchMap(({ certificationGuid }) => {
        if (!this.catalogs.available()) return EMPTY;
        return this.catalogs.retireCertification(certificationGuid).pipe(
          map((list) =>
            CompanyApiActions.certificationsLoaded({ certifications: list.map(certificationFromDto) }),
          ),
          catchError((err: unknown) => of(CompanyApiActions.rowSaveFailed({ error: toMessage(err) }))),
        );
      }),
    ),
  );

  /**
   * Saving an employment type.
   *
   * Ours is a bare string with no guid, so an edit finds the row by matching the
   * label as it WAS against the list the server sent. A label that matches nothing
   * is treated as an add, which is the safe way round: it makes a row rather than
   * silently editing the wrong one.
   */
  readonly saveEmploymentType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyPageActions.employmentTypeSaved),
      switchMap(({ label, replacing }) => {
        if (!this.catalogs.available()) return EMPTY;
        return this.catalogs.listEmploymentTypes().pipe(
          switchMap((rows) => {
            const existing = replacing ? rows.find((r) => r.label === replacing) : undefined;
            return existing
              ? this.catalogs.updateEmploymentType(existing.employmentTypeGuid, { label })
              : this.catalogs.createEmploymentType({ label });
          }),
          switchMap(() => this.employmentTypes$()),
          map((employmentTypes) => CompanyApiActions.employmentTypesLoaded({ employmentTypes })),
          catchError((err: unknown) => of(CompanyApiActions.rowSaveFailed({ error: toMessage(err) }))),
        );
      }),
    ),
  );

  /**
   * The employment types, from the server when there is one.
   *
   * Read through the same path a write re-reads, so the list cannot change shape
   * the first time somebody saves — it was the seeded list until then, and the
   * server's afterwards.
   */
  private employmentTypes$(): Observable<EmploymentType[]> {
    // The guid is KEPT. It used to be mapped away here to fit a `string[]` that
    // predated the API, which left the People form offering labels as option
    // values while a person's record holds the guid -- so the two never matched.
    return this.catalogs.listEmploymentTypes().pipe(
      map((rows) => rows.map((r) => ({ employmentTypeGuid: r.employmentTypeGuid, label: r.label }))),
    );
  }

  /** The certification catalogue. Read here; writes are `saveCertification$`. */
  private certifications$() {
    return this.catalogs.listCertifications().pipe(map((list) => list.map(certificationFromDto)));
  }

  private softwareTools$() {

    return this.catalogs.listSoftware().pipe(map((list) => list.map(softwareToolFromDto)));
  }

  /** The equipment catalogue, from the API when one is configured. */
  /** The pay classes, re-read after a removal. */
  private payClasses$() {
    return this.api.listPayClasses().pipe(map((rows) => rows.map(payClassFromDto)));
  }

  private equipmentTypes$() {

    return this.inventory.listItems().pipe(map((list) => list.map(equipmentTypeFromDto)));
  }

  /** Assets from the API when one is configured, else the seeded list. */
  private assets$() {

    return this.inventory.listAssets().pipe(map((list) => list.map(assetFromDto)));
  }

  /**
   * The API-served slices, mapped to the app's models.
   *
   * There is no seeded alternative any more: Company is wired, so its seed was
   * deleted (standards §8c). A failed load shows the page's empty states, which
   * is the truth, rather than rows the server has never heard of.
   */
  private company$(): Observable<{ info: CompanyInfo; locations: CompanyLocation[]; payClasses: PayClass[] }> {
    return this.api.get().pipe(
      map((dto) => ({
        info: companyInfoFromDto(dto),
        locations: locationsFromDto(dto),
        payClasses: payClassesFromDto(dto),
      })),
    );
  }
}

/**
 * Normalize any thrown value to a string worth showing.
 *
 * The API reports failures as RFC 7807 ProblemDetail, and writes `detail` to be
 * read by a person — the refusal to delete a location in use names the location
 * and says what to do instead. Preferring it over `HttpErrorResponse.message`
 * is the difference between that sentence and "Http failure response for
 * https://…: 409 Conflict".
 */
function toMessage(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    const detail = problemDetail(err);
    if (detail) return detail;
  }
  return err instanceof Error ? err.message : 'Unknown error';
}

/** App-shaped history filters → the server's query names. */
function toEventFilterDto(f: InventoryEventFilters): EventFilterDto {
  return {
    personaCode: f.personaCode,
    equipmentItemGuid: f.equipmentGuid,
    assetGuid: f.assetGuid,
    kind: eventKindToDto(f.kind ?? ''),
  };
}

/**
 * What to show when a removal is refused.
 *
 * A 409 means something still points at the row, and the server's `detail` says
 * which and what to do instead — it is quoted rather than replaced. The label is
 * put in front of it so the message names what was being removed rather than
 * starting mid-sentence.
 */
function removalMessage(err: unknown, label: string): string {
  const e = err as { status?: number; error?: { detail?: unknown; message?: unknown } };
  const detail = e?.error?.detail ?? e?.error?.message;
  if (typeof detail === 'string' && detail.trim()) return `“${label}” was not removed. ${detail.trim()}`;
  if (e?.status === 409) {
    return `“${label}” is still in use, so it was not removed. Retire it instead to stop offering it.`;
  }
  return `“${label}” was not removed.`;
}
